import { memo, useMemo } from 'react'
import clsx from 'clsx'
import { useIntl } from 'react-intl'

import { useImportCSVData } from 'app/store/importCSV'
import ErrorIcon2 from 'app/components/icons/ErrorIcon2'
import WarningIcon from 'app/components/icons/WarningIcon'
import {
  STEP_PROGRESS_THREE,
  STEP_PROGRESS_TWO,
} from 'app/constants/subscriberCRM'

import useStyles from './styles'
import { getTotalErrorCount } from 'app/store/importCSV/helpers'

const ErrorMessageBar = () => {
  const classes = useStyles()
  const { formatMessage, locale } = useIntl()

  const {
    data: { headers, rows, infoMappedHeaders, validationInfo },
    activeStep,
  } = useImportCSVData()

  const { failedSubscribers } = validationInfo

  const getMessageContentStep2 = (
    headers,
    infoMappedHeaders,
    formatMessage,
  ) => {
    const totalHeaders = headers.length
    const numberValidHeaders = infoMappedHeaders.filter(
      ({ isInvalid }) => !isInvalid,
    ).length

    const isImportantMessage = infoMappedHeaders.some(
      ({ required, isInvalid }) => required && isInvalid,
    )

    if (numberValidHeaders === totalHeaders && !isImportantMessage) {
      return {
        title: '',
        description: '',
        isImportantMessage: false,
        showMessage: false,
      }
    }

    return {
      showMessage: true,
      isImportantMessage,
      title: formatMessage(
        { id: 'subscribers.import.previewTable.mappedColumnsMessage.title' },
        {
          mappedColumns: numberValidHeaders,
          totalColumns: totalHeaders,
        },
      ),
      description: formatMessage(
        {
          id: isImportantMessage
            ? 'subscribers.import.previewTable.mappedColumnsMessage.errorMessage'
            : 'subscribers.import.previewTable.mappedColumnsMessage.warningMessage',
        },
        {
          b: (text) => <b>{text}</b>,
        },
      ),
    }
  }

  const getMessageContentStep3 = (failedSubscribers, rows, formatMessage) => {
    const failedSubscribersLength = getTotalErrorCount(rows)

    if (failedSubscribersLength === 0) {
      return {
        title: '',
        description: '',
        isImportantMessage: false,
        showMessage: false,
      }
    }

    const isImportantMessage = rows.every(
      ({ errors }) => errors?.isRequiredFields === true,
    )

    return {
      showMessage: true,
      isImportantMessage,
      title: formatMessage(
        {
          id: isImportantMessage
            ? 'subscribers.import.previewTable.validationRows.errorMessage.title'
            : 'subscribers.import.previewTable.validationRows.warningMessage.title',
        },
        {
          failedSubscribers: failedSubscribersLength,
          totalSubscribers: rows.length,
        },
      ),
      description: formatMessage({
        id: isImportantMessage
          ? 'subscribers.import.previewTable.validationRows.errorMessage'
          : 'subscribers.import.previewTable.validationRows.warningMessage',
      }),
    }
  }

  const messageContent = useMemo(() => {
    if (activeStep === STEP_PROGRESS_TWO) {
      return getMessageContentStep2(headers, infoMappedHeaders, formatMessage)
    } else if (activeStep === STEP_PROGRESS_THREE) {
      return getMessageContentStep3(failedSubscribers, rows, formatMessage)
    }

    return {
      title: '',
      description: '',
      isImportantMessage: false,
      showMessage: false,
    }
  }, [
    infoMappedHeaders,
    activeStep,
    failedSubscribers,
    rows,
    headers,
    formatMessage,
    locale,
  ])

  const { title, description, isImportantMessage, showMessage } = messageContent
  if (!showMessage) return

  return (
    <div
      className={clsx(classes.errorBar, {
        [classes.error]: isImportantMessage,
      })}
    >
      <div>{isImportantMessage ? <ErrorIcon2 /> : <WarningIcon />}</div>
      <div>
        <p className={classes.title}>{title}</p>
        <p className={classes.description}>{description}</p>
      </div>
    </div>
  )
}

export default memo(ErrorMessageBar)
